// Η κάτοψη ως ενιαίος χώρος. Καθαρές συναρτήσεις (τεστάρονται), κοινές για την Υπηρεσία
// και τον editor.
//
// ΚΟΙΝΟ ΑΡΧΕΙΟ, πανομοιότυπο σε Sala (xentrix_table src/lib/floor.ts) και ταμείο (xentrix_flow
// frontend/src/utils/salaFloor/floor.ts): οι δύο editors κάτοψης γράφουν το ίδιο μοντέλο.
// Αλλάζει ΜΟΝΟ στη Sala και αντιγράφεται με: node dev/sync-floor-lib.cjs (έλεγχος: --check).
// Χωρίς κείμενα οθόνης: οι έλεγχοι δίνουν κωδικό (code/vars) και ελληνικό κείμενο.
//
// Μονάδες χώρου: 50 = 1 μέτρο. Κάθε ζώνη του ταμείου είναι «περιοχή» με θέση/μέγεθος στον
// χώρο (dine_in_zones.layout). Τα τραπέζια μένουν όπως τα ξέρει το ταμείο:
//   position_x/y = κέντρο σε % της περιοχής,
//   width/height = μονάδες μεγέθους, px = μονάδες × 6 σε καμβά ζώνης πλάτους 1000.
// Άρα για περιοχή πλάτους W: 1 px του ταμείου = W/1000 μονάδες χώρου.

export const UNITS_PER_M = 50;
const POS_CANVAS_W = 1000;
const POS_UNIT_PX = 6;

export type Shape = 'round' | 'square' | 'rectangle';
export type RoomKind = 'indoor' | 'outdoor';
export type OpeningType = 'entrance' | 'door' | 'window';
export type Side = 'top' | 'right' | 'bottom' | 'left';
export type FixtureType = 'bar' | 'column' | 'plant' | 'label';

export interface Opening { id: string; type: OpeningType; side: Side; at: number; len: number; label: string }
/** Σταθερό στοιχείο. Στο API σε μονάδες από τη γωνία της περιοχής, στο μοντέλο σε μονάδες χώρου. */
export interface Fixture { id: string; type: FixtureType; x: number; y: number; w: number; h: number; label: string }
export interface ZoneLayout { x: number; y: number; w: number; h: number; kind: RoomKind; openings: Opening[]; fixtures: Fixture[] }

// ——— API (/api/floor/:storeId) ———
export interface FloorZoneApi { id: number; name: string; display_order: number; printer_id: number | null; layout: ZoneLayout; layout_saved?: boolean }
export interface FloorTableApi {
  id: number; zone_id: number; table_number: string; name: string | null; capacity: number; shape: Shape;
  position_x: number; position_y: number; width: number; height: number;
  is_reservable: boolean; online_bookable: boolean; min_capacity: number; assign_priority: number;
  has_history?: boolean; in_use?: boolean; upcoming?: { name: string; date: string; time: string }[];
}
export interface FloorApi {
  store: { id: number; dine_in: boolean; reservations: boolean };
  zones: FloorZoneApi[];
  tables: FloorTableApi[];
  combinations: { id: number; table_ids: number[]; online_bookable: boolean; min_capacity: number; max_capacity: number }[];
  printers: { id: number; name: string }[];
}
export interface SaveFloorBody {
  zones: { id: number | null; key: string; name: string; printer_id: number | null; layout: ZoneLayout }[];
  tables: (Omit<FloorTableApi, 'id' | 'zone_id' | 'has_history' | 'in_use' | 'upcoming'> & { id: number | null; key: string; zone_key: string })[];
  combinations?: { id: number | null; table_keys: string[]; online_bookable: boolean }[];
}

// ——— μοντέλο του editor (όλα σε μονάδες χώρου) ———
export interface Room { key: string; id: number | null; name: string; kind: RoomKind; x: number; y: number; w: number; h: number; openings: Opening[]; printerId: number | null }
export interface FTable {
  key: string; id: number | null; n: string; name: string | null; shape: Shape;
  /** Κέντρο και μέγεθος σε μονάδες χώρου. */
  x: number; y: number; w: number; h: number;
  cap: number; res: boolean; online: boolean; minOnline: number; prio: number;
  hasHistory: boolean; inUse: boolean; upcoming: { name: string; date: string; time: string }[];
}
export interface Combo { key: string; id: number | null; tables: string[]; online: boolean }
export interface FloorDoc { rooms: Room[]; fixtures: Fixture[]; tables: FTable[]; combos: Combo[] }

const r1 = (n: number) => Math.round(n * 10) / 10;
const r2 = (n: number) => Math.round(n * 100) / 100;
const clamp = (n: number, a: number, b: number) => Math.min(b, Math.max(a, n));

/** Μέγεθος τραπεζιού (μαζί με τον χώρο των καρεκλών) από σχήμα και θέσεις. */
export function tableSize(shape: Shape, cap: number): [number, number] {
  if (shape === 'round') return cap <= 2 ? [72, 72] : cap <= 4 ? [86, 86] : [100, 100];
  if (shape === 'square') return cap <= 2 ? [64, 64] : cap <= 4 ? [80, 80] : [92, 92];
  return [Math.max(100, 52 + cap * 12), 76];
}

/** Μήκος σε μέτρα: «1,2 μ» (ελληνικά) ή «1.2 m». */
export const m = (u: number, lang: 'el' | 'en' = 'el') =>
  (lang === 'en' ? `${(u / UNITS_PER_M).toFixed(1)} m` : `${(u / UNITS_PER_M).toFixed(1).replace('.', ',')} μ`);

export function fromApi(api: FloorApi): FloorDoc {
  const rooms: Room[] = api.zones.map(z => ({
    key: `z${z.id}`, id: z.id, name: z.name, kind: z.layout.kind || 'indoor',
    x: z.layout.x, y: z.layout.y, w: z.layout.w, h: z.layout.h,
    openings: (z.layout.openings || []).map(o => ({ ...o })), printerId: z.printer_id ?? null,
  }));
  const byId = new Map(api.zones.map((z, i) => [z.id, rooms[i]]));
  const fixtures: Fixture[] = [];
  // Χωρίς στρογγύλεμα εδώ: μια αποθήκευση χωρίς αλλαγές πρέπει να γράφει ακριβώς τα ίδια.
  // Το id ενός σταθερού στοιχείου είναι το κλειδί του στον editor: μοναδικό σε όλο τον χώρο.
  const seen = new Set<string>();
  api.zones.forEach((z, i) => (z.layout.fixtures || []).forEach(f => {
    let id = f.id || 'f';
    while (seen.has(id)) id = `${id}·`;
    seen.add(id);
    fixtures.push({ ...f, id, x: rooms[i].x + f.x, y: rooms[i].y + f.y });
  }));
  const tables: FTable[] = api.tables.flatMap(t => {
    const room = byId.get(t.zone_id);
    if (!room) return [];
    const k = room.w / POS_CANVAS_W * POS_UNIT_PX;
    return [{
      key: `t${t.id}`, id: t.id, n: t.table_number, name: t.name, shape: t.shape,
      x: room.x + Number(t.position_x) / 100 * room.w, y: room.y + Number(t.position_y) / 100 * room.h,
      w: Number(t.width) * k, h: Number(t.height) * k,
      cap: t.capacity, res: t.is_reservable !== false, online: t.online_bookable !== false,
      minOnline: t.min_capacity || 1, prio: t.assign_priority || 0,
      hasHistory: !!t.has_history, inUse: !!t.in_use, upcoming: t.upcoming || [],
    }];
  });
  const keyOf = new Map(api.tables.map(t => [t.id, `t${t.id}`]));
  const combos: Combo[] = api.combinations.map(c => ({
    key: `c${c.id}`, id: c.id, online: c.online_bookable, tables: c.table_ids.map(id => keyOf.get(id)).filter((x): x is string => !!x),
  }));
  return { rooms, fixtures, tables, combos };
}

export const inside = (r: { x: number; y: number; w: number; h: number }, x: number, y: number) =>
  x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;

export function roomAt(rooms: Room[], x: number, y: number): Room | null {
  return rooms.find(r => inside(r, x, y)) || null;
}

function nearestRoom(rooms: Room[], x: number, y: number): Room {
  let best = rooms[0];
  let bd = Infinity;
  for (const r of rooms) {
    const dx = Math.max(r.x - x, 0, x - (r.x + r.w));
    const dy = Math.max(r.y - y, 0, y - (r.y + r.h));
    const d = dx * dx + dy * dy;
    if (d < bd) { bd = d; best = r; }
  }
  return best;
}

export function toApi(doc: FloorDoc, withCombos: boolean): SaveFloorBody {
  const owner = new Map<string, Fixture[]>(doc.rooms.map(r => [r.key, []]));
  for (const f of doc.fixtures) {
    const r = roomAt(doc.rooms, f.x + f.w / 2, f.y + f.h / 2) || nearestRoom(doc.rooms, f.x + f.w / 2, f.y + f.h / 2);
    owner.get(r.key)!.push({ ...f, x: r1(f.x - r.x), y: r1(f.y - r.y) });
  }
  const body: SaveFloorBody = {
    zones: doc.rooms.map(r => ({
      id: r.id, key: r.key, name: r.name.trim(), printer_id: r.printerId,
      layout: { x: r1(r.x), y: r1(r.y), w: r1(r.w), h: r1(r.h), kind: r.kind, openings: r.openings, fixtures: owner.get(r.key)! },
    })),
    tables: doc.tables.map(t => {
      const r = roomAt(doc.rooms, t.x, t.y) || nearestRoom(doc.rooms, t.x, t.y);
      const k = r.w / POS_CANVAS_W * POS_UNIT_PX;
      return {
        id: t.id, key: t.key, zone_key: r.key, table_number: t.n.trim(), name: t.name, shape: t.shape, capacity: t.cap,
        position_x: r2(clamp((t.x - r.x) / r.w * 100, 0, 100)), position_y: r2(clamp((t.y - r.y) / r.h * 100, 0, 100)),
        width: r2(clamp(t.w / k, 2, 99)), height: r2(clamp(t.h / k, 2, 99)),
        is_reservable: t.res, online_bookable: t.online, min_capacity: Math.min(t.minOnline, t.cap), assign_priority: t.prio,
      };
    }),
  };
  if (withCombos) body.combinations = doc.combos.map(c => ({ id: c.id, table_keys: c.tables, online_bookable: c.online }));
  return body;
}

export interface Box { l: number; t: number; w: number; h: number }
export const boxOf = (t: { x: number; y: number; w: number; h: number }): Box => ({ l: t.x - t.w / 2, t: t.y - t.h / 2, w: t.w, h: t.h });

export type IssueCode = 'no_number' | 'dup_number' | 'no_name' | 'dup_name' | 'outside' | 'overlap';
export interface Issue { level: 'error' | 'warn'; keys: string[]; code: IssueCode; vars: Record<string, string>; text: string }

/** Τι εμποδίζει την αποθήκευση (error) και τι αξίζει μια ματιά (warn). */
export function floorIssues(doc: FloorDoc): Issue[] {
  const out: Issue[] = [];
  const add = (level: Issue['level'], keys: string[], code: IssueCode, vars: Record<string, string>, text: string) =>
    out.push({ level, keys, code, vars, text });
  const byN = new Map<string, FTable[]>();
  for (const t of doc.tables) {
    const k = t.n.trim();
    if (!k) add('error', [t.key], 'no_number', {}, 'Ένα τραπέζι δεν έχει αριθμό.');
    else byN.set(k, [...(byN.get(k) || []), t]);
  }
  byN.forEach((list, k) => {
    if (list.length > 1) add('error', list.map(t => t.key), 'dup_number', { n: k }, `Δύο τραπέζια έχουν τον αριθμό ${k}.`);
  });
  const names = new Map<string, Room[]>();
  for (const r of doc.rooms) {
    const k = r.name.trim().toLocaleLowerCase('el');
    if (!k) add('error', [r.key], 'no_name', {}, 'Μια περιοχή δεν έχει όνομα.');
    else names.set(k, [...(names.get(k) || []), r]);
  }
  names.forEach(list => {
    const name = list[0].name.trim();
    if (list.length > 1) add('error', list.map(r => r.key), 'dup_name', { name }, `Δύο περιοχές λέγονται «${name}».`);
  });
  for (const t of doc.tables) {
    const n = t.n || '?';
    if (!roomAt(doc.rooms, t.x, t.y)) add('error', [t.key], 'outside', { n }, `Το Τ${n} είναι έξω από τις περιοχές.`);
  }
  for (let i = 0; i < doc.tables.length; i++) {
    for (let j = i + 1; j < doc.tables.length; j++) {
      const a = boxOf(doc.tables[i]);
      const b = boxOf(doc.tables[j]);
      if (a.l < b.l + b.w - 2 && b.l < a.l + a.w - 2 && a.t < b.t + b.h - 2 && b.t < a.t + a.h - 2) {
        const [na, nb] = [doc.tables[i].n, doc.tables[j].n];
        add('warn', [doc.tables[i].key, doc.tables[j].key], 'overlap', { a: na, b: nb }, `Το Τ${na} και το Τ${nb} πέφτουν το ένα πάνω στο άλλο.`);
      }
    }
  }
  return out;
}

export function nextNumber(tables: FTable[]): string {
  const nums = tables.map(t => Number.parseInt(t.n, 10)).filter(n => !Number.isNaN(n));
  return String((nums.length ? Math.max(...nums) : 0) + 1);
}

/** Ελεύθερο σημείο για νέο τραπέζι μέσα στην περιοχή (πρώτο από πάνω αριστερά). */
export function freeSpot(doc: FloorDoc, room: Room, w: number, h: number): [number, number] {
  const pad = 24;
  for (let y = room.y + pad + h / 2; y <= room.y + room.h - pad - h / 2; y += 10) {
    for (let x = room.x + pad + w / 2; x <= room.x + room.w - pad - w / 2; x += 10) {
      const c = { l: x - w / 2, t: y - h / 2, w, h };
      const hit = doc.tables.some(t => {
        const b = boxOf(t);
        return c.l < b.l + b.w + 14 && b.l < c.l + c.w + 14 && c.t < b.t + b.h + 14 && b.t < c.t + c.h + 14;
      }) || doc.fixtures.some(f => c.l < f.x + f.w + 10 && f.x < c.l + c.w + 10 && c.t < f.y + f.h + 10 && f.y < c.t + c.h + 10);
      if (!hit) return [Math.round(x / 10) * 10, Math.round(y / 10) * 10];
    }
  }
  return [Math.round((room.x + room.w / 2) / 10) * 10, Math.round((room.y + room.h / 2) / 10) * 10];
}

export interface Bounds { x0: number; y0: number; x1: number; y1: number }

/** Το κουτί που χωρά τις περιοχές (με χώρο για τις ετικέτες τους και τα ανοίγματα). */
export function boundsOf(rooms: { x: number; y: number; w: number; h: number }[]): Bounds {
  if (!rooms.length) return { x0: 0, y0: 0, x1: 620, y1: 372 };
  return {
    x0: Math.min(...rooms.map(r => r.x)) - 30,
    y0: Math.min(...rooms.map(r => r.y)) - 40,
    x1: Math.max(...rooms.map(r => r.x + r.w)) + 40,
    y1: Math.max(...rooms.map(r => r.y + r.h)) + 30,
  };
}

/** Κλίμακα και μετατόπιση ώστε το κουτί να χωρά στο παράθυρο (κεντραρισμένο). */
export function fit(b: Bounds, vw: number, vh: number, max = 1.6) {
  const w = b.x1 - b.x0;
  const h = b.y1 - b.y0;
  const s = Math.max(0.05, Math.min(vw / w, vh / h, max));
  return { s, tx: (vw - w * s) / 2 - b.x0 * s, ty: (vh - h * s) / 2 - b.y0 * s };
}

/** Γεωμετρία για την Υπηρεσία: περιοχές και τραπέζια (κατά id) σε μονάδες χώρου. */
export function geometry(
  zones: { id: number; name: string; layout?: ZoneLayout | null }[],
  tables: { id: number; zone_id: number; position_x: string | number; position_y: string | number; width: string | number; height: string | number }[],
) {
  const rooms = zones.filter(z => z.layout).map(z => ({ id: z.id, name: z.name, ...(z.layout as ZoneLayout) }));
  const byZone = new Map(rooms.map(r => [r.id, r]));
  const boxes = new Map<number, { x: number; y: number; w: number; h: number; zoneId: number }>();
  for (const t of tables) {
    const r = byZone.get(t.zone_id);
    if (!r) continue;
    const k = r.w / POS_CANVAS_W * POS_UNIT_PX;
    boxes.set(t.id, {
      x: r.x + Number(t.position_x) / 100 * r.w, y: r.y + Number(t.position_y) / 100 * r.h,
      w: (Number(t.width) || 10) * k, h: (Number(t.height) || 10) * k, zoneId: t.zone_id,
    });
  }
  return { rooms, boxes };
}
